/** Variantes de hero con componente dedicado en components/microsite/Hero*.tsx.
 *  HeroSection enruta por esta unión; agregar un tema = agregar su variante aquí
 *  y su entrada en HERO_BY_VARIANT (el Record obliga exhaustividad en typecheck). */
export type HeroVariant =
  | 'classic'
  | 'vibrant'
  | 'editorial'
  | 'dark'
  | 'bold'
  | 'costa'
  | 'cafeto'
  | 'sumi'
  | 'ruta'
  | 'deco'
  | 'jardin'
  | 'barro'
  | 'oliva'
  | 'neon'
  | 'mercado'
  | 'vino'
  | 'medianoche'
  | 'obsidiana'
  | 'brasa'
  // Composiciones que NO pertenecen a ningún tema: solo leen tokens, así que
  // cualquiera puede elegirlas (ver HERO_ALTERNATES).
  | 'split'
  | 'marca'

export type ThemeTokens = {
  bg: string
  surface: string
  surface2: string
  text: string
  textMuted: string
  textSoft: string
  border: string
  accentDefault: string
  fontDisplay: string
  fontBody: string
  radius: string
  heroVariant: HeroVariant
  heroOverlay: string
  cardShadow: string
  /** Barra superior sólida (oscura) desde el top — para temas cuyo hero es claro,
   *  donde el nav transparente con texto blanco no se vería. */
  navSolid: boolean
  /** URL opcional de un CSS de Google Fonts a cargar cuando el tema está activo
   *  (p.ej. Playfair para editorial). El provider la inyecta como <link>. */
  googleFont?: string
  /** Ids de FONT_PAIRS ofrecidos como ALTERNATIVAS para este tema (la pareja
   *  del propio tema siempre va primera y no se lista aquí). Curadas a mano:
   *  solo combinaciones que respetan el mundo del tema. */
  fontPairs?: string[]
}

export type MicrositeThemeDef = { id: string; name: string; tokens: ThemeTokens }

const SATOSHI = "'Satoshi', 'Inter', system-ui, sans-serif"
const INTER = "'Inter', system-ui, sans-serif"
const PLAYFAIR = "'Playfair Display', Georgia, 'Times New Roman', serif"
const CORMORANT = "'Cormorant Garamond', Georgia, 'Times New Roman', serif"
const BEBAS = "'Bebas Neue', 'Arial Narrow', 'Helvetica Neue', sans-serif"
const MARCELLUS = "'Marcellus', 'Times New Roman', serif"
const FRAUNCES = "'Fraunces', Georgia, serif"
const SHIPPORI = "'Shippori Mincho', 'Hiragino Mincho ProN', Georgia, serif"
const ALFA_SLAB = "'Alfa Slab One', 'Rockwell', Georgia, serif"
const POIRET = "'Poiret One', 'Futura', 'Avenir Next', sans-serif"
const ITALIANA = "'Italiana', 'Didot', 'Bodoni MT', serif"
const INSTRUMENT = "'Instrument Serif', Georgia, 'Times New Roman', serif"
const NEWSREADER = "'Newsreader', Georgia, 'Times New Roman', serif"
const UNBOUNDED = "'Unbounded', 'Arial Black', 'Helvetica Neue', sans-serif"
const SHRIKHAND = "'Shrikhand', Georgia, serif"
const PRATA = "'Prata', Georgia, 'Times New Roman', serif"
const CINZEL = "'Cinzel', 'Times New Roman', serif"
const SYNE = "'Syne', system-ui, sans-serif"
const GLOOCK = "'Gloock', Georgia, serif"

/** URLs de Google Fonts por familia display. Las usan los temas y el catálogo
 *  de parejas tipográficas — una sola definición para que no driftee. */
const GF_PLAYFAIR =
  'https://fonts.googleapis.com/css2?family=Playfair+Display:wght@600;700;800;900&display=swap'
const GF_CORMORANT =
  'https://fonts.googleapis.com/css2?family=Cormorant+Garamond:ital,wght@0,500;0,600;0,700;1,500;1,600&display=swap'
const GF_BEBAS =
  'https://fonts.googleapis.com/css2?family=Bebas+Neue&display=swap'
const GF_MARCELLUS =
  'https://fonts.googleapis.com/css2?family=Marcellus&display=swap'
const GF_FRAUNCES =
  'https://fonts.googleapis.com/css2?family=Fraunces:opsz,wght@9..144,600;9..144,700&display=swap'
const GF_SHIPPORI =
  'https://fonts.googleapis.com/css2?family=Shippori+Mincho:wght@500;600&display=swap'
const GF_ALFA =
  'https://fonts.googleapis.com/css2?family=Alfa+Slab+One&display=swap'
const GF_POIRET =
  'https://fonts.googleapis.com/css2?family=Poiret+One&display=swap'
const GF_ITALIANA =
  'https://fonts.googleapis.com/css2?family=Italiana&display=swap'
const GF_INSTRUMENT =
  'https://fonts.googleapis.com/css2?family=Instrument+Serif:ital,wght@0,400;1,400&display=swap'
const GF_NEWSREADER =
  'https://fonts.googleapis.com/css2?family=Newsreader:ital,opsz,wght@1,6..72,500;1,6..72,600&display=swap'
const GF_UNBOUNDED =
  'https://fonts.googleapis.com/css2?family=Unbounded:wght@500;600;700&display=swap'
const GF_SHRIKHAND =
  'https://fonts.googleapis.com/css2?family=Shrikhand&display=swap'
const GF_PRATA =
  'https://fonts.googleapis.com/css2?family=Prata&display=swap'
const GF_CINZEL =
  'https://fonts.googleapis.com/css2?family=Cinzel:wght@500;600&display=swap'
const GF_SYNE =
  'https://fonts.googleapis.com/css2?family=Syne:wght@700;800&display=swap'
const GF_GLOOCK =
  'https://fonts.googleapis.com/css2?family=Gloock&display=swap'

const classic: MicrositeThemeDef = {
  id: 'classic',
  name: 'Clásico',
  tokens: {
    bg: '#ffffff',
    surface: '#ffffff',
    surface2: '#f9fafb',
    text: '#111827',
    textMuted: '#6b7280',
    textSoft: '#374151',
    border: '#f3f4f6',
    accentDefault: '#ea580c',
    fontDisplay: "'Satoshi', var(--font-inter), system-ui, sans-serif",
    fontBody: "'Satoshi', var(--font-inter), system-ui, sans-serif",
    radius: '1rem',
    heroVariant: 'classic',
    heroOverlay:
      'linear-gradient(to top, rgba(0,0,0,0.75), rgba(0,0,0,0.55) 50%, rgba(0,0,0,0.2))',
    cardShadow: '0 1px 2px 0 rgba(0,0,0,0.05)',
    navSolid: false,
    fontPairs: ['playfair', 'fraunces'],
  },
}

const vibrant: MicrositeThemeDef = {
  id: 'vibrant',
  name: 'Vibrante',
  tokens: {
    bg: '#fffaf6',
    surface: '#ffffff',
    surface2: '#fff3ec',
    text: '#1a1a1a',
    textMuted: '#7c6f68',
    textSoft: '#5a4f48',
    border: '#f0e6df',
    accentDefault: '#ea580c',
    fontDisplay: SATOSHI,
    fontBody: INTER,
    radius: '1.25rem',
    heroVariant: 'vibrant',
    heroOverlay:
      'linear-gradient(115deg, rgba(10,0,0,0.15) 0%, rgba(60,12,0,0.45) 45%, rgba(124,30,0,0.92) 100%)',
    cardShadow: '0 16px 34px -14px rgba(124,30,0,0.28)',
    navSolid: false,
    fontPairs: ['fraunces', 'bebas'],
  },
}

const editorial: MicrositeThemeDef = {
  id: 'editorial',
  name: 'Editorial',
  tokens: {
    bg: '#faf8f5',
    surface: '#ffffff',
    surface2: '#f3efe9',
    text: '#1a1a1a',
    textMuted: '#8a8175',
    textSoft: '#4a443c',
    border: '#e7e0d6',
    accentDefault: '#b06a3f',
    fontDisplay: PLAYFAIR,
    fontBody: SATOSHI,
    radius: '0.875rem',
    heroVariant: 'editorial',
    heroOverlay:
      'linear-gradient(to top, rgba(0,0,0,0.35), rgba(0,0,0,0.1) 50%, transparent)',
    cardShadow: '0 1px 3px 0 rgba(0,0,0,0.05)',
    navSolid: true,
    fontPairs: ['cormorant', 'newsreader', 'prata'],
    googleFont: GF_PLAYFAIR,
  },
}

// Dark luxe — fine-dining nocturno. Negro CÁLIDO (no #000 plano) + dorado
// champagne + marfil. Display serif de alto contraste (Cormorant Garamond,
// distinto del Playfair del editorial). Sombras profundas, esquinas afiladas.
const dark: MicrositeThemeDef = {
  id: 'dark',
  name: 'Dark luxe',
  tokens: {
    bg: '#0e0b09',
    surface: '#1a1410',
    surface2: '#15100b',
    text: '#f4ece0',
    textMuted: '#9c9082',
    textSoft: '#cdbfa9',
    border: '#2c2520',
    accentDefault: '#c8a86a',
    fontDisplay: CORMORANT,
    fontBody: SATOSHI,
    radius: '0.5rem',
    heroVariant: 'dark',
    heroOverlay:
      'linear-gradient(to top, rgba(8,5,4,0.94) 0%, rgba(8,5,4,0.62) 45%, rgba(8,5,4,0.34) 100%)',
    cardShadow: '0 24px 50px -24px rgba(0,0,0,0.8)',
    navSolid: true,
    fontPairs: ['prata', 'italiana', 'marcellus'],
    googleFont: GF_CORMORANT,
  },
}

// Bold — brutalist moderno / streetwear. Hueso cálido + tinta casi-negra +
// acento saturado. Display CONDENSADO gigante (Bebas Neue), bordes definidos y
// "hard shadows" (sin blur, desplazadas) como sello. Esquinas casi rectas.
const bold: MicrositeThemeDef = {
  id: 'bold',
  name: 'Bold',
  tokens: {
    bg: '#f2ede1',
    surface: '#ffffff',
    surface2: '#e7dccb',
    text: '#16130f',
    textMuted: '#6b6253',
    textSoft: '#3a342b',
    border: '#16130f',
    accentDefault: '#ff4d00',
    fontDisplay: BEBAS,
    fontBody: SATOSHI,
    radius: '0.25rem',
    heroVariant: 'bold',
    heroOverlay:
      'linear-gradient(to top, rgba(0,0,0,0.58) 0%, rgba(0,0,0,0.12) 60%, transparent 100%)',
    cardShadow: '5px 5px 0 0 #16130f',
    navSolid: true,
    fontPairs: ['unbounded', 'syne'],
    googleFont: GF_BEBAS,
  },
}

// Costa — fresco costero (mariscos, caribeño, brunch de playa). Sal de mar con
// cast frío + petróleo profundo + aqua; coral #ff6f61 vive solo en el hero como
// color de firma. PRIMER tema de hero claro: velo sol-lavado, no overlay negro.
const costa: MicrositeThemeDef = {
  id: 'costa',
  name: 'Costa',
  tokens: {
    bg: '#f4f9f8',
    surface: '#ffffff',
    surface2: '#e9f4f1',
    text: '#0b3a3f',
    textMuted: '#5f8285',
    textSoft: '#2f5c60',
    border: '#dcebe9',
    accentDefault: '#0e9a8d',
    fontDisplay: MARCELLUS,
    fontBody: SATOSHI,
    radius: '1.25rem',
    heroVariant: 'costa',
    heroOverlay:
      'linear-gradient(to top, rgba(6,42,45,0.55), rgba(6,42,45,0.16) 55%, rgba(255,255,255,0))',
    cardShadow: '0 18px 40px -18px rgba(11,88,80,0.22)',
    navSolid: true,
    fontPairs: ['newsreader', 'prata'],
    googleFont: GF_MARCELLUS,
  },
}

// Cafeto — café/panadería/brunch. Crema mantequilla + espresso + caramelo.
// Fraunces (serif suave con carácter) y el radius MÁS redondo del catálogo;
// bordes dashed tipo papel kraft como sello. Acogedor, no solemne (≠ editorial).
const cafeto: MicrositeThemeDef = {
  id: 'cafeto',
  name: 'Cafeto',
  tokens: {
    bg: '#fdf9f3',
    surface: '#fffdf9',
    surface2: '#f7eede',
    text: '#3b2f26',
    textMuted: '#9a8a77',
    textSoft: '#5f4e3e',
    border: '#efe3d0',
    accentDefault: '#d98324',
    fontDisplay: FRAUNCES,
    fontBody: INTER,
    radius: '1.5rem',
    heroVariant: 'cafeto',
    heroOverlay:
      'linear-gradient(to top, rgba(59,47,38,0.82), rgba(59,47,38,0.45) 50%, rgba(59,47,38,0.12))',
    cardShadow: '0 14px 30px -16px rgba(122,90,51,0.25)',
    navSolid: true,
    fontPairs: ['newsreader', 'instrument'],
    googleFont: GF_FRAUNCES,
  },
}

// Sumi — japonés/zen minimal. Papel washi + tinta + bermellón de sello (hanko).
// Mincho japonesa de display, aire vertical extremo y el PRIMER tema sin
// sombras: la elevación es 100% hairlines. Esquinas mínimas.
const sumi: MicrositeThemeDef = {
  id: 'sumi',
  name: 'Sumi',
  tokens: {
    bg: '#f8f7f4',
    surface: '#ffffff',
    surface2: '#f1efe9',
    text: '#1a1b1e',
    textMuted: '#8a8a85',
    textSoft: '#4a4b46',
    border: '#e8e6e0',
    accentDefault: '#c9372c',
    fontDisplay: SHIPPORI,
    fontBody: INTER,
    radius: '0.375rem',
    heroVariant: 'sumi',
    heroOverlay:
      'linear-gradient(to top, rgba(16,16,18,0.8), rgba(16,16,18,0.4) 55%, rgba(16,16,18,0.08))',
    cardShadow: 'none',
    navSolid: true,
    fontPairs: ['italiana', 'cormorant'],
    googleFont: GF_SHIPPORI,
  },
}

// Ruta — diner retro americana. Crema + marrón root-beer + rojo cereza; la
// menta #8fd8c6 vive solo en el hero como color de firma (damero, sombras
// duras del letrero). Slab de rótulo (Alfa Slab One).
const ruta: MicrositeThemeDef = {
  id: 'ruta',
  name: 'Ruta',
  tokens: {
    bg: '#fdf6e8',
    surface: '#fffdf6',
    surface2: '#f7ecd4',
    text: '#4a2c1c',
    textMuted: '#a08663',
    textSoft: '#6b4a33',
    border: '#ecd9b8',
    accentDefault: '#c8102e',
    fontDisplay: ALFA_SLAB,
    fontBody: SATOSHI,
    radius: '0.75rem',
    heroVariant: 'ruta',
    heroOverlay:
      'linear-gradient(to top, rgba(58,26,12,0.85), rgba(58,26,12,0.5) 50%, rgba(58,26,12,0.15))',
    cardShadow: '0 10px 24px -12px rgba(122,64,24,0.28)',
    navSolid: true,
    fontPairs: ['bebas', 'gloock'],
    googleFont: GF_ALFA,
  },
}

// Deco — glamour geométrico art déco (steakhouse, speakeasy, rooftop, hotel).
// Marfil champagne + tinta noir + latón; la esmeralda #123f33 vive solo en el
// hero como color de firma. Marcos de doble línea, simetría total y radius 0
// (esquinas rectas en TODO el tema). Poiret One nunca bajo 20px (es thin).
const deco: MicrositeThemeDef = {
  id: 'deco',
  name: 'Deco',
  tokens: {
    bg: '#f8f3e6',
    surface: '#fffbf0',
    surface2: '#f3ecd9',
    text: '#1b1912',
    textMuted: '#9d8f6d',
    textSoft: '#4d452f',
    border: '#d9cfae',
    accentDefault: '#9a7b2d',
    fontDisplay: POIRET,
    fontBody: SATOSHI,
    radius: '0px',
    heroVariant: 'deco',
    heroOverlay:
      'linear-gradient(to top, rgba(20,18,10,0.88), rgba(20,18,10,0.5) 50%, rgba(20,18,10,0.2))',
    cardShadow: '0 2px 12px rgba(80,68,30,0.1)',
    navSolid: true,
    fontPairs: ['italiana', 'cinzel'],
    googleFont: GF_POIRET,
  },
}

// Jardín — botánico nocturno (bistró verde, cocktail bar, de temporada).
// Verde bosque + crema + salvia: familia cromática 100% distinta a Dark luxe
// (negro cálido/oro). Display hairline (Italiana), divisor de enredadera y
// marcos ovalados de lámina botánica como firmas.
const jardin: MicrositeThemeDef = {
  id: 'jardin',
  name: 'Jardín',
  tokens: {
    bg: '#101d17',
    surface: '#17281f',
    surface2: '#0d1813',
    text: '#ede7d8',
    textMuted: '#8fa08d',
    textSoft: '#c8d1b9',
    border: '#2a3c31',
    accentDefault: '#9ab87a',
    fontDisplay: ITALIANA,
    fontBody: SATOSHI,
    radius: '0.75rem',
    heroVariant: 'jardin',
    heroOverlay:
      'linear-gradient(to top, rgba(8,16,12,0.92), rgba(8,16,12,0.6) 48%, rgba(8,16,12,0.3))',
    cardShadow: '0 24px 48px -22px rgba(0,0,0,0.65)',
    navSolid: true,
    fontPairs: ['cormorant', 'marcellus'],
    googleFont: GF_ITALIANA,
  },
}

// Barro — orgánico de autor (farm-to-table, vino natural, brunch de autor).
// Avena + tinta de arcilla + umber quemado; el musgo #737049 vive solo en el
// hero. Firmas: asimetría deliberada, minúsculas con una palabra en itálica y
// máscaras blob (border-radius orgánico irregular) en las fotos.
const barro: MicrositeThemeDef = {
  id: 'barro',
  name: 'Barro',
  tokens: {
    bg: '#f3ede4',
    surface: '#faf6ef',
    surface2: '#ece4d5',
    text: '#2b2119',
    textMuted: '#a19381',
    textSoft: '#5c4f41',
    border: '#e2d8c6',
    accentDefault: '#8c3f21',
    fontDisplay: INSTRUMENT,
    fontBody: SATOSHI,
    radius: '1.25rem',
    heroVariant: 'barro',
    heroOverlay:
      'linear-gradient(to top, rgba(43,33,25,0.8), rgba(43,33,25,0.42) 52%, rgba(43,33,25,0.08))',
    cardShadow: '0 16px 36px -18px rgba(90,63,40,0.2)',
    navSolid: true,
    fontPairs: ['fraunces', 'newsreader'],
    googleFont: GF_INSTRUMENT,
  },
}

// Oliva — rústico mediterráneo (italiano, tapas, wine bar). Yeso cálido +
// tinta oliva + terracota; el oliva #66682f vive solo en el hero como color de
// firma. Newsreader en ITÁLICA como voz display y arcos (border-radius superior
// tipo capilla) como sello en las fotos.
const oliva: MicrositeThemeDef = {
  id: 'oliva',
  name: 'Oliva',
  tokens: {
    bg: '#f7f2e9',
    surface: '#fffcf5',
    surface2: '#efe8d8',
    text: '#33321f',
    textMuted: '#96907a',
    textSoft: '#55523c',
    border: '#e5ddc9',
    accentDefault: '#c05a36',
    fontDisplay: NEWSREADER,
    fontBody: SATOSHI,
    radius: '0.875rem',
    heroVariant: 'oliva',
    heroOverlay:
      'linear-gradient(to top, rgba(40,38,20,0.8), rgba(40,38,20,0.42) 52%, rgba(40,38,20,0.1))',
    cardShadow: '0 2px 10px rgba(60,55,30,0.08)',
    navSolid: true,
    fontPairs: ['instrument', 'fraunces'],
    googleFont: GF_NEWSREADER,
  },
}

// Neón — nocturno eléctrico (late night, delivery-first, urbano). Negro
// AZULADO (distinto del negro cálido de Dark luxe y del bosque de Jardín) +
// rosa eléctrico; el cian #29d9d5 vive solo en el hero. Unbounded de display,
// texto en degradado rosa→cian y glow SOLO en el CTA (único tema con
// sombra-glow como cardShadow).
const neon: MicrositeThemeDef = {
  id: 'neon',
  name: 'Neón',
  tokens: {
    bg: '#0b0b12',
    surface: '#14141f',
    surface2: '#101019',
    text: '#f2f1ff',
    textMuted: '#8d8ba6',
    textSoft: '#c6c4dd',
    border: '#262538',
    accentDefault: '#ff2d78',
    fontDisplay: UNBOUNDED,
    fontBody: INTER,
    radius: '1rem',
    heroVariant: 'neon',
    heroOverlay:
      'linear-gradient(to top, rgba(8,8,16,0.92), rgba(8,8,16,0.6) 45%, rgba(8,8,16,0.3))',
    cardShadow: '0 0 24px rgba(255,45,120,0.18)',
    navSolid: true,
    fontPairs: ['syne', 'bebas'],
    googleFont: GF_UNBOUNDED,
  },
}

// Mercado — herencia latina viva (taquerías, antojitos, casual heredado).
// Crema cálida + tinta + verde profundo; el rosa #e0447c y el amarillo
// #f2b134 viven solo en el hero (franja tricolor + banderines de papel
// picado). Shrikhand SOLO para el nombre del negocio — todo lo demás en body.
const mercado: MicrositeThemeDef = {
  id: 'mercado',
  name: 'Mercado',
  tokens: {
    bg: '#fcf7ee',
    surface: '#ffffff',
    surface2: '#f5ecdb',
    text: '#26221b',
    textMuted: '#9a8d75',
    textSoft: '#4f4636',
    border: '#eadfc9',
    accentDefault: '#167a5b',
    fontDisplay: SHRIKHAND,
    fontBody: SATOSHI,
    radius: '1rem',
    heroVariant: 'mercado',
    heroOverlay:
      'linear-gradient(to top, rgba(20,58,45,0.85), rgba(20,58,45,0.5) 50%, rgba(20,58,45,0.15))',
    cardShadow: '0 10px 26px -14px rgba(22,90,66,0.22)',
    navSolid: true,
    fontPairs: ['bebas', 'fraunces'],
    googleFont: GF_SHRIKHAND,
  },
}

// Vino — burdeos nocturno (chophouse, vinoteca, italiano de noche). Burdeos
// casi negro + marfil cálido + rosa vino; el cobre #c98a5e vive solo en el hero
// como color de firma (monograma, hairlines). Prata (didone de carta de vinos);
// la familia roja-vinosa lo separa del negro marrón de Dark luxe.
const vino: MicrositeThemeDef = {
  id: 'vino',
  name: 'Vino',
  tokens: {
    bg: '#150a10',
    surface: '#1f1018',
    surface2: '#190d12',
    text: '#f6ece4',
    textMuted: '#a68e93',
    textSoft: '#dcc3bc',
    border: '#38222d',
    accentDefault: '#d4707f',
    fontDisplay: PRATA,
    fontBody: SATOSHI,
    radius: '0.625rem',
    heroVariant: 'vino',
    heroOverlay:
      'linear-gradient(to top, rgba(18,7,13,0.96) 0%, rgba(18,7,13,0.74) 46%, rgba(18,7,13,0.4) 100%)',
    cardShadow: '0 26px 54px -26px rgba(0,0,0,0.82)',
    navSolid: true,
    fontPairs: ['cormorant', 'marcellus', 'instrument'],
    googleFont: GF_PRATA,
  },
}

// Medianoche — navy grabado (oyster bar, raw bar, hotel dining). Azul marino
// profundo + marfil frío + champán. Cinzel en caps con tracking ancho, marco
// doble de invitación grabada y fases de luna como firmas; el glow del hero es
// "luz de luna" fría, no el acento. Distinto del negro azulado eléctrico de
// Neón: aquí es clásico grabado, no letrero encendido.
const medianoche: MicrositeThemeDef = {
  id: 'medianoche',
  name: 'Medianoche',
  tokens: {
    bg: '#0a1120',
    surface: '#0f1b30',
    surface2: '#0c1626',
    text: '#eef0e9',
    textMuted: '#8b96a8',
    textSoft: '#cfd6d2',
    border: '#223350',
    accentDefault: '#d6bc80',
    fontDisplay: CINZEL,
    fontBody: SATOSHI,
    radius: '0.25rem',
    heroVariant: 'medianoche',
    heroOverlay:
      'linear-gradient(to top, rgba(7,13,26,0.97) 0%, rgba(7,13,26,0.72) 46%, rgba(7,13,26,0.45) 100%)',
    cardShadow: '0 28px 60px -30px rgba(0,0,0,0.85)',
    navSolid: true,
    fontPairs: ['marcellus', 'italiana', 'prata'],
    googleFont: GF_CINZEL,
  },
}

// Obsidiana — monocromo editorial (omakase, chef's table, tasting menu). Negro
// NEUTRO + blanco, Syne expandida y fotos B/N en el hero: el único tema casi
// acromático del catálogo. El acento es grafito-plata (no blanco puro):
// SchedulePicker y ReservationModal pintan texto blanco sobre --ms-accent, y
// #86868f mantiene ese texto legible (≈3.6:1) y sigue visible sobre el fondo
// (≈5.5:1). El CTA "blanco" del hero va invertido (texto sobre fondo), no
// sobre el acento — mismo patrón que HeroDark.
const obsidiana: MicrositeThemeDef = {
  id: 'obsidiana',
  name: 'Obsidiana',
  tokens: {
    bg: '#0a0a0b',
    surface: '#121214',
    surface2: '#0e0e10',
    text: '#f5f5f7',
    textMuted: '#8e8e96',
    textSoft: '#cfcfd6',
    border: '#232327',
    accentDefault: '#86868f',
    fontDisplay: SYNE,
    fontBody: INTER,
    radius: '0.125rem',
    heroVariant: 'obsidiana',
    heroOverlay:
      'linear-gradient(to bottom, rgba(8,8,9,0.62), transparent 22%), linear-gradient(to top, rgba(8,8,9,0.97) 4%, rgba(8,8,9,0.55) 48%, rgba(8,8,9,0.28) 100%)',
    cardShadow: '0 30px 60px -30px rgba(0,0,0,0.9)',
    navSolid: true,
    fontPairs: ['bebas', 'satoshi'],
    googleFont: GF_SYNE,
  },
}

// Brasa — carbón y ember (asador, parrilla, cocina de fuego). Carbón CENIZA +
// marfil ahumado; el acento es fuego #ff5f2e y su degradado ember
// (#ffa24d→#ff5f2e→#e0401f) vive solo en el hero (CTA, última palabra del
// nombre) como firma. Gloock de display. Distinto de Dark luxe: campo ceniza y
// acento fuego, no negro marrón con oro champagne.
const brasa: MicrositeThemeDef = {
  id: 'brasa',
  name: 'Brasa',
  tokens: {
    bg: '#121010',
    surface: '#1c1714',
    surface2: '#161211',
    text: '#f5eee6',
    textMuted: '#a3948a',
    textSoft: '#e0cdbd',
    border: '#342b24',
    accentDefault: '#ff5f2e',
    fontDisplay: GLOOCK,
    fontBody: SATOSHI,
    radius: '0.75rem',
    heroVariant: 'brasa',
    heroOverlay:
      'linear-gradient(to top, rgba(14,10,9,0.97) 0%, rgba(14,10,9,0.6) 46%, rgba(14,10,9,0.38) 100%)',
    cardShadow: '0 26px 54px -26px rgba(0,0,0,0.85)',
    navSolid: true,
    fontPairs: ['alfa', 'fraunces', 'instrument'],
    googleFont: GF_GLOOCK,
  },
}

export const THEMES: Record<string, MicrositeThemeDef> = {
  classic,
  vibrant,
  editorial,
  dark,
  bold,
  costa,
  cafeto,
  sumi,
  ruta,
  deco,
  jardin,
  barro,
  oliva,
  neon,
  mercado,
  vino,
  medianoche,
  obsidiana,
  brasa,
}

/**
 * Orden de presentación del catálogo (picker del admin y galería pública):
 * classic primero por ser el default, luego los claros/cálidos y los oscuros al
 * cierre. Es el ORDEN, no la fuente de verdad: los tokens viven en THEMES.
 */
export const THEME_ORDER: string[] = [
  'classic',
  'vibrant',
  'editorial',
  'bold',
  'costa',
  'cafeto',
  'sumi',
  'ruta',
  'oliva',
  'barro',
  'mercado',
  'deco',
  'dark',
  'jardin',
  'neon',
  'vino',
  'medianoche',
  'obsidiana',
  'brasa',
]

/** Catálogo ordenado y listo para mapear. Ignora ids de THEME_ORDER sin tema. */
export function themeCatalog(): MicrositeThemeDef[] {
  return THEME_ORDER.map((id) => THEMES[id]).filter(Boolean)
}

// ---------------------------------------------------------------------------
// Parejas tipográficas
//
// Cada tema trae SU pareja (fontDisplay + fontBody) soldada en los tokens. Esto
// permite al merchant cambiarla por otra de un catálogo CURADO: la tipografía
// es, después del color, la palanca que hace que su página no se vea igual a la
// del vecino con el mismo tema.
//
// El catálogo es cerrado a propósito (nada de "elegí entre 800 fuentes"): cada
// pareja reusa familias que el catálogo de temas ya carga y que están vetadas
// para pantallas de restaurante. Qué parejas ve cada tema lo decide su
// `tokens.fontPairs` — curado a mano, jamás combinatorio.
// ---------------------------------------------------------------------------

export type FontPairDef = {
  id: string
  /** Familia display, tal cual se muestra en el picker. Los nombres de fuente
   *  son nombres propios: NO se traducen, así que no llevan clave i18n. */
  name: string
  fontDisplay: string
  fontBody: string
  googleFont?: string
}

/**
 * Composiciones de hero que se ofrecen a TODOS los temas.
 *
 * El hero de cada tema es suyo: casi todos llevan un color de firma cableado
 * (el cobre de Vino, el ember de Brasa, el coral de Costa) que, puesto en otro
 * tema, metería un color ajeno a su paleta. Por eso las alternativas no son
 * heroes de otros temas sino composiciones propias, escritas SOLO con tokens
 * (--ms-*): se ven como el tema que las use.
 *
 * 'split' — foto a un lado, texto al otro: el texto nunca va sobre la imagen.
 * 'marca' — el logo del negocio protagoniza en vez del nombre.
 */
export const HERO_ALTERNATES: HeroVariant[] = ['split', 'marca']

/** Id reservado de "la pareja del propio tema" (la opción por defecto). */
export const THEME_FONT_PAIR_ID = 'theme'

export const FONT_PAIRS: Record<string, FontPairDef> = {
  playfair: { id: 'playfair', name: 'Playfair Display', fontDisplay: PLAYFAIR, fontBody: SATOSHI, googleFont: GF_PLAYFAIR },
  cormorant: { id: 'cormorant', name: 'Cormorant', fontDisplay: CORMORANT, fontBody: SATOSHI, googleFont: GF_CORMORANT },
  prata: { id: 'prata', name: 'Prata', fontDisplay: PRATA, fontBody: SATOSHI, googleFont: GF_PRATA },
  marcellus: { id: 'marcellus', name: 'Marcellus', fontDisplay: MARCELLUS, fontBody: SATOSHI, googleFont: GF_MARCELLUS },
  instrument: { id: 'instrument', name: 'Instrument Serif', fontDisplay: INSTRUMENT, fontBody: SATOSHI, googleFont: GF_INSTRUMENT },
  newsreader: { id: 'newsreader', name: 'Newsreader', fontDisplay: NEWSREADER, fontBody: SATOSHI, googleFont: GF_NEWSREADER },
  fraunces: { id: 'fraunces', name: 'Fraunces', fontDisplay: FRAUNCES, fontBody: INTER, googleFont: GF_FRAUNCES },
  cinzel: { id: 'cinzel', name: 'Cinzel', fontDisplay: CINZEL, fontBody: SATOSHI, googleFont: GF_CINZEL },
  italiana: { id: 'italiana', name: 'Italiana', fontDisplay: ITALIANA, fontBody: SATOSHI, googleFont: GF_ITALIANA },
  syne: { id: 'syne', name: 'Syne', fontDisplay: SYNE, fontBody: INTER, googleFont: GF_SYNE },
  unbounded: { id: 'unbounded', name: 'Unbounded', fontDisplay: UNBOUNDED, fontBody: INTER, googleFont: GF_UNBOUNDED },
  bebas: { id: 'bebas', name: 'Bebas Neue', fontDisplay: BEBAS, fontBody: SATOSHI, googleFont: GF_BEBAS },
  alfa: { id: 'alfa', name: 'Alfa Slab One', fontDisplay: ALFA_SLAB, fontBody: SATOSHI, googleFont: GF_ALFA },
  gloock: { id: 'gloock', name: 'Gloock', fontDisplay: GLOOCK, fontBody: SATOSHI, googleFont: GF_GLOOCK },
  // Sans puro: para el merchant que quiere su página sin serif. No trae
  // googleFont porque Satoshi ya vive en la app.
  satoshi: { id: 'satoshi', name: 'Satoshi', fontDisplay: SATOSHI, fontBody: SATOSHI },
}

// ---------------------------------------------------------------------------
// Intensidad del velo del hero
//
// Cada tema trae un `heroOverlay` diseñado para que su título se lea sobre una
// foto cualquiera. Pero las fotos reales varían muchísimo: una toma nocturna
// aguanta mucho menos velo que un plato sobre mármol blanco. Esto deja al
// merchant moverlo SIN poder dejar su propia página ilegible.
//
// El nivel es 0..100 con 50 = lo que diseñó el tema. El piso es RELATIVO
// (0.45× lo diseñado) y no absoluto a propósito: los overlays van de 0.35 de
// alfa máxima (editorial) a 0.97 (medianoche), así que un piso fijo sería
// inútil para uno y asfixiante para el otro.
// ---------------------------------------------------------------------------

/** Nivel neutro: el velo tal cual lo diseñó el tema. */
export const HERO_OVERLAY_DEFAULT = 50

/** El velo nunca cae por debajo de este múltiplo del diseñado. */
const OVERLAY_MIN_FACTOR = 0.45
/** Ni sube más de este (el alfa igual se topa en 1). */
const OVERLAY_MAX_FACTOR = 1.6

/** Multiplicador de alfa para un nivel. null/inválido ⇒ 1 (sin cambio). */
export function heroOverlayFactor(level: number | null | undefined): number {
  if (level == null || !Number.isFinite(level)) return 1
  const l = Math.min(100, Math.max(0, level))
  if (l === HERO_OVERLAY_DEFAULT) return 1
  return l < HERO_OVERLAY_DEFAULT
    ? OVERLAY_MIN_FACTOR + (1 - OVERLAY_MIN_FACTOR) * (l / HERO_OVERLAY_DEFAULT)
    : 1 + (OVERLAY_MAX_FACTOR - 1) * ((l - HERO_OVERLAY_DEFAULT) / (100 - HERO_OVERLAY_DEFAULT))
}

/**
 * Reescala el alfa de cada parada `rgba()` del gradiente del tema. Todo lo que
 * no sea un `rgba()` de 4 componentes se deja intacto: `transparent`, los
 * porcentajes de parada y la dirección no se tocan, y un tema con DOS capas
 * (obsidiana) escala las dos.
 */
export function scaleHeroOverlay(
  overlay: string,
  level: number | null | undefined,
): string {
  const factor = heroOverlayFactor(level)
  if (factor === 1) return overlay
  return overlay.replace(/rgba\(([^)]+)\)/g, (whole, inner: string) => {
    const parts = inner.split(',').map((p) => p.trim())
    if (parts.length !== 4) return whole
    const alpha = Number(parts[3])
    if (!Number.isFinite(alpha)) return whole
    const scaled = Math.min(1, Math.max(0, alpha * factor))
    return `rgba(${parts[0]}, ${parts[1]}, ${parts[2]}, ${Number(scaled.toFixed(3))})`
  })
}

/** Id reservado de "la composición del propio tema" (la opción por defecto). */
export const THEME_HERO_ID = 'theme'

/** Composiciones que puede elegir un tema: la suya primero, luego las comunes. */
export function heroVariantsFor(themeId: string | null | undefined): HeroVariant[] {
  return [resolveTheme(themeId).tokens.heroVariant, ...HERO_ALTERNATES]
}

/**
 * Composición efectiva para (tema, elección del merchant). Cae a la del tema
 * cuando no eligió, cuando el id no existe, o cuando pidió el hero de OTRO tema
 * — eso último es lo que impide que la página herede un color de firma ajeno.
 */
export function resolveHeroVariant(
  themeId: string | null | undefined,
  chosen: string | null | undefined,
): HeroVariant {
  const own = resolveTheme(themeId).tokens.heroVariant
  const id = (chosen ?? '').trim()
  if (!id || id === THEME_HERO_ID) return own
  return (HERO_ALTERNATES as string[]).includes(id) ? (id as HeroVariant) : own
}

/** "'Prata', Georgia, serif" → "Prata". Para rotular la pareja del tema. */
function displayFamilyName(fontStack: string): string {
  const first = fontStack.split(',')[0]?.trim() ?? ''
  return first.replace(/^['"]|['"]$/g, '') || 'Sans'
}

/** La pareja propia del tema, sintetizada de sus tokens (no vive en FONT_PAIRS
 *  para que no pueda driftear respecto del tema). */
export function themeFontPair(theme: MicrositeThemeDef): FontPairDef {
  return {
    id: THEME_FONT_PAIR_ID,
    name: displayFamilyName(theme.tokens.fontDisplay),
    fontDisplay: theme.tokens.fontDisplay,
    fontBody: theme.tokens.fontBody,
    googleFont: theme.tokens.googleFont,
  }
}

/** Parejas que puede elegir un tema: la suya primero, luego sus alternativas. */
export function fontPairsFor(themeId: string | null | undefined): FontPairDef[] {
  const theme = resolveTheme(themeId)
  const alts = (theme.tokens.fontPairs ?? [])
    .map((id) => FONT_PAIRS[id])
    .filter(Boolean)
  return [themeFontPair(theme), ...alts]
}

/**
 * Pareja efectiva para (tema, elección del merchant). Cae a la del tema cuando
 * no eligió, cuando el id no existe, o cuando la pareja NO está ofrecida para
 * ese tema — así, cambiar de tema nunca deja una tipografía fuera de catálogo.
 */
export function resolveFontPair(
  themeId: string | null | undefined,
  pairId: string | null | undefined,
): FontPairDef {
  const theme = resolveTheme(themeId)
  const id = (pairId ?? '').trim()
  if (!id || id === THEME_FONT_PAIR_ID) return themeFontPair(theme)
  const allowed = theme.tokens.fontPairs ?? []
  if (!allowed.includes(id)) return themeFontPair(theme)
  return FONT_PAIRS[id] ?? themeFontPair(theme)
}

/**
 * Un solo <link> de Google Fonts con TODAS las familias display del catálogo
 * (temas + parejas tipográficas), para superficies que muestran muchas a la vez
 * (picker del admin, galería pública). El micrositio público sigue cargando solo
 * la de la pareja activa — ahí sería desperdicio traerlas todas.
 */
export function allThemeFontsHref(): string {
  // Se extraen los pares `family=...` TAL CUAL vienen en la URL de cada tema:
  // ya están codificados como los quiere Google ("Playfair+Display",
  // "Fraunces:opsz,wght@9..144,600"). Pasarlos por URLSearchParams los
  // decodifica (el "+" se vuelve espacio) y arruina la petición.
  const families = new Set<string>()
  const sources = [
    ...Object.values(THEMES).map((d) => d.tokens.googleFont),
    ...Object.values(FONT_PAIRS).map((p) => p.googleFont),
  ]
  for (const url of sources) {
    const query = url?.split('?')[1]
    if (!query) continue
    for (const part of query.split('&')) {
      if (part.startsWith('family=')) families.add(part)
    }
  }
  return `https://fonts.googleapis.com/css2?${[...families].sort().join('&')}&display=swap`
}

export function resolveTheme(id: string | null | undefined): MicrositeThemeDef {
  return (id && THEMES[id]) || THEMES.classic
}

export function themeCssVars(
  theme: MicrositeThemeDef,
  accentColor?: string | null,
  fontPairId?: string | null,
  heroOverlayLevel?: number | null,
): Record<string, string> {
  const t = theme.tokens
  // La pareja tipográfica del merchant pisa la del tema por la misma vía que el
  // acento: tokens → CSS vars. Sin elección (o con una inválida) devuelve la del
  // propio tema, así que el resultado es idéntico al de antes de esta feature.
  const pair = resolveFontPair(theme.id, fontPairId)
  return {
    '--ms-bg': t.bg,
    '--ms-surface': t.surface,
    '--ms-surface-2': t.surface2,
    '--ms-text': t.text,
    '--ms-text-muted': t.textMuted,
    '--ms-text-soft': t.textSoft,
    '--ms-border': t.border,
    '--ms-accent': accentColor || t.accentDefault,
    '--ms-font-display': pair.fontDisplay,
    '--ms-font-body': pair.fontBody,
    '--ms-radius': t.radius,
    '--ms-card-shadow': t.cardShadow,
    '--ms-hero-overlay': scaleHeroOverlay(t.heroOverlay, heroOverlayLevel),
  }
}
